/**
 * 토큰 사용량 한 줄 — **언제나 보인다**(docs/subscription-tiers.md 결정 ⑧).
 *
 * 자기 LLM 계정으로 돈을 쓰는 사람은 얼마나 썼는지 볼 수 있어야 한다. 막기 위한 계측이 아니라
 * 보여 주기 위한 것이라 한도·차단과는 무관하다.
 *
 * **비용은 제공자가 줄 때만 적는다.** Claude 는 실제 금액을 주고 Codex 는 토큰만 준다.
 * 없는 값을 단가표로 추정해 적지 않는다 — 단가는 조용히 낡고, 틀린 금액은 없는 것보다 나쁘다.
 * 토큰 수는 두 제공자 모두 실제 값이라 그것을 앞세운다.
 *
 * 캐시 토큰을 따로 적는 이유: 대화가 길어지면 입력의 대부분이 캐시 읽기다. 합치면 "입력 20만" 처럼
 * 보여 놀라고, 빼면 실제보다 적게 말하게 된다.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiActivity } from 'react-icons/fi';
import styles from './AssistantPanel.module.css';

/** 1,234 / 12.3k / 1.2M — 자릿수가 커도 한 줄에 들어가야 한다. */
function compact(n) {
    const v = Number(n) || 0;
    if (v < 1000) return String(v);
    if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`;
    return `${(v / 1_000_000).toFixed(1)}M`;
}

/** $0.0123 — 아주 작은 금액이 0 으로 보이면 안 된다. */
function money(usd) {
    const v = Number(usd) || 0;
    if (v === 0) return '$0';
    return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

function UsageBar({ usage }) {
    const { t } = useTranslation();
    if (!usage || !usage.thread) return null;

    const { turn, thread } = usage;
    const cache = (thread.cacheReadTokens || 0) + (thread.cacheCreateTokens || 0);
    // 비용은 제공자가 준 값이 있을 때만. null 이면 아예 적지 않는다.
    const hasCost = typeof thread.costUsd === 'number' && thread.costUsd > 0;

    const title = [
        t('assistant:usage.tipTurn', {
            input: (turn && turn.inputTokens) || 0,
            output: (turn && turn.outputTokens) || 0,
        }),
        t('assistant:usage.tipThread', {
            input: thread.inputTokens || 0,
            output: thread.outputTokens || 0,
            turns: thread.turns || 0,
        }),
        cache ? t('assistant:usage.tipCache', { cache }) : null,
        hasCost ? null : t('assistant:usage.tipNoCost'),
        // 감시 에이전트(장시간 작업을 함께 지켜본 판단) — 대화 합계에 이미 들어 있다
        usage.watch ? t('assistant:usage.tipWatch', { n: usage.watch.judgments, input: usage.watch.inputTokens || 0, output: usage.watch.outputTokens || 0 }) : null,
    ].filter(Boolean).join('\n');

    return (
        <div className={styles.usageBar} title={title}>
            <FiActivity size={10} />
            <span>{t('assistant:usage.tokens', {
                input: compact(thread.inputTokens),
                output: compact(thread.outputTokens),
            })}</span>
            {cache > 0 && <span className={styles.usageDim}>{t('assistant:usage.cache', { cache: compact(cache) })}</span>}
            {hasCost && <span className={styles.usageCost}>{money(thread.costUsd)}</span>}
            {usage.watch ? <span className={styles.usageDim}>{t('assistant:usage.watch', { n: usage.watch.judgments })}</span> : null}
        </div>
    );
}

UsageBar.propTypes = {
    usage: PropTypes.shape({
        turn: PropTypes.object,
        thread: PropTypes.object,
        watch: PropTypes.object,
    }),
};

export default UsageBar;
